import {
  IManipulation,
  IManipulationFlat,
  useManipulationMonitor,
} from "@/hooks/useManipulationMonitor.ts";
import { createColumnHelper } from "@tanstack/react-table";
import { PaneBody } from "@/components/pane/paneBody.tsx";
import { IColumn } from "@/domain/interfaces/sfTable.interface.ts";
import { isNil } from "lodash";
import { Loader } from "@/components/shared/loader.tsx";
import React, { useEffect, useMemo, useRef } from "react";
import { usePaneContext } from "@/components/pane/paneContext.ts";
import { useGrid } from "@/hooks/useGrid.ts";
import { applyFilters } from "@/lib/utils.ts";
import { useFilterStream } from "@/hooks/useFilterStream.ts";
import { PaneSubHeader } from "@/components/pane/paneSubHeader.tsx";
import { useDashboard } from "@/lib/store";
import { useFilterData } from "@/hooks/useFilterData";
import { useAlertHistoryStore } from "@/lib/stores/useAlertHistoryStore";
import { toast } from "../ui/use-toast";

const columnHelper = createColumnHelper<IManipulationFlat>();

const columns = [
  columnHelper.accessor("symbol", {
    header: "Symbol",
    enableSorting: false,
  }),
  columnHelper.accessor("corr", {
    header: "Price",
    cell: (row) => {
      const value = row.getValue();
      const bgClass = getBgClass(value);
      if (isNil(value)) {
        return <div>-</div>;
      }
      return (
        <div className={bgClass + " font-semibold"}>{value?.toFixed(1)}%</div>
      );
    },
    size: 50,
    minSize: 20,
  }),
  columnHelper.accessor("corrV", {
    header: "Volume",
    cell: (row) => {
      const value = row.getValue();
      const bgClass = getBgClass(value);
      if (isNil(value)) {
        return <div>-</div>;
      }
      return (
        <div className={bgClass + " font-semibold"}>{value?.toFixed(1)}%</div>
      );
    },
    size: 50,
    minSize: 20,
  }),
];

const getBgClass = (val: number) => {
  if (val > 0.99) {
    return "bg-[#b2182c]";
  }
  if (val > 0.75) {
    return "bg-[#ffa028]";
  }
  if (val > 0.5) {
    return "bg-[#3ca64b]";
  }
  if (val > 0.25) {
    return "bg-[#0d9ddb]";
  }
  if (val > 0.01) {
    return "bg-[rgba(255,255,255,0.0)]";
  }
};

export function ManipulationMonitorPaneBody() {
  const { data: manipulationMonitor, isLoading } = useManipulationMonitor();
  const filteredData = useFilterData<IManipulationFlat>(
    manipulationMonitor || [],
  );
  const { globalFilters, getGlobalFilterValues, blacklist } = useDashboard();
  const nodeId = usePaneContext();
  const { getNodeConfig } = useGrid(nodeId);
  const config = getNodeConfig();
  const { addAlert } = useAlertHistoryStore();
  const prevSymbolsRef = useRef<Set<string>>(new Set());
  const preparedData = useMemo(() => {
    if (!filteredData || isLoading) {
      return [];
    }
    let preparedData = applyFilters(
      filteredData,
      globalFilters,
      getGlobalFilterValues(),
    );

    if (blacklist.length > 0) {
      blacklist.forEach((symbol) => {
        preparedData = preparedData.filter((item) => item.symbol !== symbol);
      });
    }

    if (config?.filters && config?.filterValues) {
      return applyFilters<IManipulationFlat>(
        preparedData,
        config.filters,
        config.filterValues,
      );
    }
    return preparedData;
  }, [
    filteredData,
    isLoading,
    globalFilters,
    getGlobalFilterValues,
    blacklist,
    config.filters,
    config.filterValues,
  ]);

  useEffect(() => {
    if (config.isSound && preparedData.length > 0) {
      const currentSymbols = new Set(preparedData.map((item) => item.symbol));
      const prevSymbols = prevSymbolsRef.current;

      // Find new symbols that weren't in the previous data
      const newSymbols = [...currentSymbols].filter(
        (symbol) => !prevSymbols.has(symbol),
      );

      // If there are new symbols, get the one with highest value
      if (newSymbols.length > 0) {
        const newSymbolsData = preparedData.filter((item) =>
          newSymbols.includes(item.symbol),
        );

        if (newSymbolsData.length > 0) {
          const topNewSymbol = newSymbolsData[0].symbol;
          const msg = `Манипуляция  ${topNewSymbol.split("USDT")[0]}`;
          toast({
            title: "Manipulation Screener Alert",
            description: `${topNewSymbol} alert price: ${newSymbolsData[0].corr.toFixed(2)} volume: ${newSymbolsData[0].corrV.toFixed(2)}`,
          });
          addAlert({
            symbol: topNewSymbol,
            timestamp: Date.now(),
            message: `${topNewSymbol} alert price: ${newSymbolsData[0].corr.toFixed(2)} volume: ${newSymbolsData[0].corrV.toFixed(2)}`,
            type: "manipulation",
          });
          const utterThis = new SpeechSynthesisUtterance(msg);
          utterThis.voice = window.speechSynthesis
            .getVoices()
            .filter((voice) => voice.lang === "ru-RU")[0];
          utterThis.lang = "ru-RU";
          window.speechSynthesis.speak(utterThis);
        }
      }

      // Update previous symbols reference
      prevSymbolsRef.current = currentSymbols;
    }
  }, [preparedData, config.isSound, addAlert]);

  return isLoading ? (
    <Loader />
  ) : (
    <>
      <PaneSubHeader />
      <PaneBody
        className="!h-[calc(100%-24px)]"
        tableColumns={columns as IColumn[]}
        data={preparedData}
      />
    </>
  );
}
